import { t as createLogger } from "./app.shared-CmP4XLF_.js";

import { t as withTimeout } from "./app.shared-CBZA3IJ4.js";

import { t as formatMoney } from "./app.shared-DrMetWk9.js";

var log = createLogger("cart-drawer");

var FACET_TIMEOUT_MS = 8e3;

var FACET_SELECTORS = [ "[data-cart-delivery]", "[data-cart-items]", "[data-cart-empty]", "[data-cart-checkout]" ];

var TOTAL_COMPARE_CLASS = "text-white-600";

var CHECKOUT_FACET = "[data-cart-checkout]";

var CONTINUE_HOOK = "[data-cart-continue]";

var cart_drawer_default = (() => {
    if (customElements.get("cart-drawer")) return;
    customElements.define("cart-drawer", class CartDrawer extends HTMLElement {
        constructor() {
            super();
            this.checkbox = null;
            this._facetController = null;
            this._facetPromise = Promise.resolve();
            this._saving = false;
            this._scrollOnSettle = false;
            this._onLockerChange = this._onLockerChange.bind(this);
            this._onCartAdded = this._onCartAdded.bind(this);
            this._onCartUpdated = this._onCartUpdated.bind(this);
            this._onCartLoading = this._onCartLoading.bind(this);
            this._onSaveStart = this._onSaveStart.bind(this);
            this._onSaveEnd = this._onSaveEnd.bind(this);
            this._onItemError = this._onItemError.bind(this);
        }
        connectedCallback() {
            this.checkbox = document.getElementById("Cart");
            this.checkbox?.addEventListener("change", this._onLockerChange);
            document.addEventListener("cart:added", this._onCartAdded);
            document.addEventListener("cart:updated", this._onCartUpdated);
            document.addEventListener("cart:loading", this._onCartLoading);
            document.addEventListener("cart:save-start", this._onSaveStart);
            document.addEventListener("cart:save-end", this._onSaveEnd);
            document.addEventListener("cart-item:error", this._onItemError);
        }
        disconnectedCallback() {
            this.checkbox?.removeEventListener("change", this._onLockerChange);
            document.removeEventListener("cart:added", this._onCartAdded);
            document.removeEventListener("cart:updated", this._onCartUpdated);
            document.removeEventListener("cart:loading", this._onCartLoading);
            document.removeEventListener("cart:save-start", this._onSaveStart);
            document.removeEventListener("cart:save-end", this._onSaveEnd);
            document.removeEventListener("cart-item:error", this._onItemError);
            this._facetController?.abort();
        }
        isOpen() {
            return Boolean(this.checkbox?.checked);
        }
        open() {
            if (!this.checkbox || this.checkbox.checked) return;
            this.checkbox.click();
        }
        _onLockerChange() {
            document.dispatchEvent(new CustomEvent(this.isOpen() ? "cart-drawer:open" : "cart-drawer:closing"));
            const scroller = this.querySelector("[data-drawer-scroll]");
            if (scroller) scroller.scrollTop = 0;
        }
        _setBusy(on) {
            this.querySelector("[data-cart-busy]")?.classList.toggle("hidden", !on);
            this.querySelector("[data-drawer-scroll]")?.toggleAttribute("inert", on);
        }
        async _releaseBusy() {
            await this._facetPromise;
            this._setBusy(false);
            if (!this._scrollOnSettle) return;
            this._scrollOnSettle = false;
            this.querySelector("[data-drawer-scroll]")?.scrollTo({
                top: 0,
                behavior: "smooth"
            });
        }
        _onCartLoading(event) {
            if (this._saving) return;
            if (event.detail?.loading) {
                this._setBusy(true);
                return;
            }
            this._releaseBusy();
        }
        _onSaveStart() {
            this._saving = true;
            this._setBusy(true);
        }
        async _onSaveEnd(event) {
            const cart = event.detail?.cart;
            this._saving = false;
            if (cart) {
                this._updateTotals(cart);
                this._updateFreeShipping(cart);
                if (this._isStructuralChange(cart)) await this.refreshFacets();
            }
            this.querySelector("[data-drawer-scroll]")?.scrollTo({
                top: 0,
                behavior: "smooth"
            });
            this._setBusy(false);
        }
        async _onItemError() {
            this._saving = false;
            this._scrollOnSettle = false;
            try {
                const cart = await window.MBF.cart.get();
                document.dispatchEvent(new CustomEvent("cart:updated", {
                    bubbles: true,
                    detail: {
                        cart: cart
                    }
                }));
            } catch (error) {
                log("errors", "cart reconcile failed", {
                    error: error
                });
                if (!error?.duplicate) {
                    const message = window.MBF?.strings?.cart?.error;
                    if (message) document.dispatchEvent(new CustomEvent("toast:show", {
                        detail: {
                            message: message
                        }
                    }));
                }
            } finally {
                this._setBusy(false);
            }
        }
        _onCartAdded() {
            if (this._saving) return;
            this._scrollOnSettle = true;
            this.open();
            this.refreshFacets();
        }
        _onCartUpdated(event) {
            if (this._saving) return;
            const cart = event.detail?.cart;
            if (!cart) return;
            this._updateTotals(cart);
            this._updateFreeShipping(cart);
            if (this._isStructuralChange(cart)) this.refreshFacets();
        }
        _updateTotals(cart) {
            const totalEl = this.querySelector("[data-cart-total]");
            if (!totalEl) return;
            let html = formatMoney(cart.total_price);
            if (cart.original_total_price > cart.total_price) html += `<s data-cart-total-compare class="${TOTAL_COMPARE_CLASS}">${formatMoney(cart.original_total_price)}</s>`;
            totalEl.innerHTML = html;
        }
        _updateFreeShipping(cart) {
            const delivery = this.querySelector("[data-cart-delivery]");
            const threshold = Number(delivery?.dataset.threshold) || 0;
            if (!delivery || threshold <= 0) return;
            const strings = window.MBF?.strings?.cart || {};
            const textEl = delivery.querySelector("[data-free-shipping-text]");
            const barEl = delivery.querySelector("[data-free-shipping-bar]");
            const qualified = cart.total_price >= threshold;
            if (textEl) textEl.textContent = qualified ? strings.freeShippingPost || "" : (strings.freeShippingPre || "").replace("[[amount]]", formatMoney(Math.max(threshold - cart.total_price, 0)));
            if (barEl) {
                const pct = Math.min(cart.total_price / threshold, 1);
                barEl.style.transform = `scaleX(${pct})`;
            }
        }
        _isStructuralChange(cart) {
            const domRows = Array.from(this.querySelectorAll("[data-line-item]"));
            const items = cart.items || [];
            if (domRows.length !== items.length) return true;
            return domRows.map(row => row.dataset.variantId).join(",") !== items.map(item => String(item.variant_id)).join(",");
        }
        _toastRefreshError() {
            const message = window.MBF?.strings?.cart?.errors?.refresh;
            if (!message) return;
            document.dispatchEvent(new CustomEvent("toast:show", {
                detail: {
                    message: message
                }
            }));
        }
        async refreshFacets() {
            this._facetController?.abort();
            this._facetController = new AbortController;
            const fetchPromise = (async () => {
                const {signal: signal, cancel: cancel} = withTimeout(this._facetController.signal, FACET_TIMEOUT_MS);
                try {
                    const response = await fetch(`${location.pathname}?sections=cart-drawer`, {
                        signal: signal
                    });
                    if (!response.ok) {
                        log("network", "facet fetch failed", {
                            status: response.status
                        });
                        this._toastRefreshError();
                        return;
                    }
                    const payloads = await response.json();
                    const html = payloads?.["cart-drawer"];
                    if (!html) {
                        log("network", "facet payload missing cart-drawer section", {
                            sections: Object.keys(payloads || {})
                        });
                        this._toastRefreshError();
                        return;
                    }
                    const parsed = (new DOMParser).parseFromString(html, "text/html");
                    const swapped = [];
                    FACET_SELECTORS.forEach(selector => {
                        const fresh = parsed.querySelector(selector);
                        const current = this.querySelector(selector);
                        if (!fresh || !current) return;
                        if (selector === CHECKOUT_FACET && Boolean(fresh.querySelector(CONTINUE_HOOK)) === Boolean(current.querySelector(CONTINUE_HOOK))) return;
                        current.innerHTML = fresh.innerHTML;
                        swapped.push(selector);
                    });
                    if (swapped.length > 0) document.dispatchEvent(new CustomEvent("cart-drawer:refreshed", {
                        bubbles: true
                    }));
                    log("ui", "facets swapped", {
                        count: swapped.length,
                        selectors: swapped
                    });
                } catch (error) {
                    if (error.name === "AbortError") return;
                    log("errors", "facet refresh failed", {
                        error: error
                    });
                    this._toastRefreshError();
                } finally {
                    cancel();
                }
            })();
            this._facetPromise = fetchPromise;
            return fetchPromise;
        }
    });
})();